/**
 * O que cada tela exige, declarado na própria rota — o espelho de
 * `backend/src/authorization/pode.ts` do lado do navegador.
 *
 * Antes daqui a guarda **deduzia** a exigência do catálogo do menu, e o menu só conhece tela
 * de listagem e só com permissão de leitura. Três buracos saíam disso, todos medidos na
 * auditoria de 608 aberturas de tela: `/companies/create` abria para quem só tinha
 * `company.read`; tela que não é item de menu (aplicação em massa, importação de funcionários,
 * agendamentos pendentes) não tinha guarda nenhuma; e módulo fora do contrato tirava o item do
 * menu sem fechar o cadastro.
 *
 * A regra de lint `local/rota-declara-acesso` recusa `<ProtectedRoute>` sem `exige`, para que a
 * próxima tela não nasça descoberta. Ela nasce sem lista de exceções e deve continuar assim:
 * rota sem porta declara o motivo, e o motivo fica no código.
 *
 * ```tsx
 * <ProtectedRoute exige={pode('company.create')}>          // cadastro exige criar
 * <ProtectedRoute exige={pode('exam_application.read', { perspectiva: 'plataforma' })}>
 * <ProtectedRoute exige={semPorta('a própria conta de quem está logado')}>
 * ```
 */

import type { Manifesto, Modulo, Perspectiva } from '../types/manifesto';
import {
  acaoLiberadaSemModulo,
  itemDoCaminho,
  itemDoCatalogo,
  moduloEncerrado,
  NOME_DA_PERSPECTIVA,
  NOME_DO_MODULO,
} from '../utils/manifestoMenu';

export type Exigencia =
  | {
      readonly tipo: 'permissao';
      readonly permissao: string;
      /** Quando presente, só o contexto ativo daquela perspectiva abre a tela. */
      readonly perspectiva?: Perspectiva;
    }
  | { readonly tipo: 'semPorta'; readonly motivo: string };

export function pode(
  permissao: string,
  opcoes: { perspectiva?: Perspectiva } = {}
): Exigencia {
  return { tipo: 'permissao', permissao, perspectiva: opcoes.perspectiva };
}

/**
 * A tela não tem porta, e o motivo fica escrito. É o equivalente do `semGate()` do backend:
 * declarar "não exige nada" é uma decisão, e ela precisa ser lida no code review — o que não
 * pode existir é rota que não diz nada.
 */
export function semPorta(motivo: string): Exigencia {
  return { tipo: 'semPorta', motivo };
}

/**
 * A ação que a permissão nomeia (`epi.create` → `create`), que é o que decide se a tela
 * sobrevive ao módulo fora do contrato. A família fica de fora de propósito: ela não se deduz
 * do prefixo do nome (`professional_schedule.read` pertence a `scheduling`), e quem responde
 * por módulo aqui é o item do catálogo de telas, não o nome da permissão.
 */
export function acaoDeclarada(permissao: string): string {
  const separador = permissao.lastIndexOf('.');
  return separador === -1 ? permissao : permissao.slice(separador + 1);
}

/**
 * Por que a porta ficou fechada. Cada nome é uma frase diferente na tela: o que resolve trocar
 * de contexto ativo não é o que resolve contratar um módulo, e nenhum dos dois é o que resolve
 * pedir acesso a quem administra.
 */
export type MotivoDoVeredito =
  | 'permissao'
  | 'vinculo'
  | 'perspectiva'
  | 'modulo'
  | 'indeclarada'
  /** O manifesto ainda não chegou: ninguém sabe de nada, e nada se oferece. */
  | 'carregando';

export interface Veredito {
  readonly liberado: boolean;
  readonly motivo?: MotivoDoVeredito;
  /** Uma frase, pronta para a tela. Ausente quando liberado. */
  readonly explicacao?: string;
  /**
   * O nome da permissão que faltou. É detalhe técnico — serve à tela de recusa e ao review,
   * nunca ao rótulo de um controle: "risk_application.read" não explica nada a quem usa.
   */
  readonly permissaoFaltante?: string;
  /** O módulo que saiu do contrato, mesmo quando a leitura continua liberada (V5). */
  readonly moduloEncerrado?: Modulo;
}

const LIBERADO: Veredito = { liberado: true };

/**
 * Desabilitar com o motivo, nunca sumir — issue #408.
 *
 * A primeira onda sumia com o controle quando faltava permissão ou vínculo, e só explicava
 * perspectiva e módulo, com a régua de que "não há saída, e botão morto só promete". A
 * medição derrubou o argumento: quem não vê o controle não descobre que a ação existe, e é
 * justamente a informação que resolve — trocar de contexto, contratar o módulo, pedir o
 * acesso a quem administra — que some junto com o botão. Sem ela a pessoa abre chamado
 * dizendo que o sistema "não tem" a função.
 *
 * A função continua existindo, e sempre responde a mesma coisa, porque é aqui que a política
 * mora: voltar a esconder alguma classe é mudar esta linha, e não caçar condição em 22 telas.
 */
export function explicarEmVezDeSumir(_motivo: MotivoDoVeredito): boolean {
  return true;
}

/**
 * As quatro perguntas de acesso a uma tela, num lugar só — issue #408.
 *
 * A guarda de rota já as fazia; os controles que levam a uma tela não faziam nenhuma, e por
 * isso o botão "Aplicar Riscos" aparecia no contexto de uma empresa só para levar a pessoa a
 * uma recusa. Enquanto a decisão morar dentro do componente de guarda, todo controle que
 * quiser a mesma resposta precisa reescrevê-la — e foi assim que as telas passaram a
 * perguntar só pela permissão, que é a única das quatro que a lista do manifesto carrega.
 *
 * A ordem importa: vínculo antes de permissão, porque a quem o prestador não alcança não se
 * deve dizer qual permissão falta.
 */
export function avaliar(
  caminho: string,
  manifesto: Pick<Manifesto, 'menu' | 'modulos' | 'permissoes' | 'perspectiva'>,
  exigencia: Exigencia | undefined
): Veredito {
  if (!exigencia) {
    return {
      liberado: false,
      motivo: 'indeclarada',
      explicacao: 'Esta rota não declara o que exige.',
    };
  }

  const itemDoMenu = itemDoCatalogo(caminho);
  const itemAlcancado = itemDoCaminho(manifesto.menu, caminho);

  // O corte por vínculo (quem o prestador alcança) vive no menu do manifesto, e não na lista
  // de permissões do contexto — por isso ele é perguntado ao lado da declaração.
  if (itemDoMenu && !itemAlcancado) {
    return {
      liberado: false,
      motivo: 'vinculo',
      explicacao: 'Esta tela não faz parte do seu acesso neste contexto.',
      permissaoFaltante: itemDoMenu.permissao,
    };
  }

  // Qual módulo cobre a tela é o servidor que diz, e ele diz no item do manifesto: o catálogo
  // de rotas daqui conhece o caminho e a permissão, e nunca o contrato.
  const encerrado = itemAlcancado ? moduloEncerrado(itemAlcancado, manifesto.modulos) : undefined;
  const liberado: Veredito = encerrado ? { liberado: true, moduloEncerrado: encerrado } : LIBERADO;

  if (exigencia.tipo === 'semPorta') return liberado;

  if (!manifesto.permissoes.includes(exigencia.permissao)) {
    return {
      liberado: false,
      motivo: 'permissao',
      explicacao: 'Seu perfil não inclui esta tela.',
      permissaoFaltante: exigencia.permissao,
    };
  }

  if (exigencia.perspectiva && manifesto.perspectiva !== exigencia.perspectiva) {
    return {
      liberado: false,
      motivo: 'perspectiva',
      explicacao:
        `Esta tela é operação de ${NOME_DA_PERSPECTIVA[exigencia.perspectiva]}, e o seu ` +
        `contexto ativo é ${NOME_DA_PERSPECTIVA[manifesto.perspectiva]}. Troque para ` +
        `${NOME_DA_PERSPECTIVA[exigencia.perspectiva]} para usar.`,
    };
  }

  // V5 — módulo fora do contrato mantém a consulta, com a faixa por cima, e fecha a escrita.
  if (encerrado && !acaoLiberadaSemModulo(acaoDeclarada(exigencia.permissao))) {
    return {
      liberado: false,
      motivo: 'modulo',
      explicacao: `O módulo ${NOME_DO_MODULO[encerrado]} não está contratado.`,
      moduloEncerrado: encerrado,
    };
  }

  return liberado;
}

/**
 * O que um controle que **age na própria tela** exige — a segunda onda da issue #408.
 *
 * `avaliar` responde por uma tela de destino, e por isso não alcança o controle que não leva
 * a lugar nenhum: cancelar um agendamento, enviar uma CAT, gerar um lote de eventos. Esses
 * eram botões crus, guardados no máximo por estado do registro, e a mesma ação aparecia
 * guardada na tela de lista e nua na tela de detalhe — as duas telas se contradiziam.
 */
export interface ExigenciaDeAcao {
  /**
   * A permissão que o servidor exige. Uma lista significa "qualquer uma delas", que é como o
   * `pode({ qualquer: [...] })` do backend guarda as ações de duas mãos — apagar o arquivo de
   * um exame vale para quem administra o agendamento e para quem administra o laudo.
   */
  readonly permissao: string | readonly string[];
  /**
   * O módulo comercial que cobre a ação, quando não é o da tela onde o controle vive. A aba
   * de eSocial é o caso: ela mora na ficha da empresa, que é núcleo, e o que ela opera é um
   * módulo contratado à parte. Sem isto a pergunta de módulo passaria batida.
   */
  readonly modulo?: Modulo;
}

/**
 * As mesmas quatro perguntas, para uma ação em vez de uma tela. `caminho` é onde o controle
 * vive — é dele que saem o vínculo e o módulo da tela.
 */
export function avaliarAcao(
  caminho: string,
  manifesto: Pick<Manifesto, 'menu' | 'modulos' | 'permissoes' | 'perspectiva'>,
  exigencia: ExigenciaDeAcao
): Veredito {
  const permissoes =
    typeof exigencia.permissao === 'string' ? [exigencia.permissao] : exigencia.permissao;
  const vereditos = permissoes.map((permissao) => avaliar(caminho, manifesto, pode(permissao)));
  const daTela = vereditos.find((veredito) => veredito.liberado) ?? vereditos[0];

  if (!daTela.liberado) {
    // A frase de `avaliar` fala de tela ("Seu perfil não inclui esta tela"), e aqui quem está
    // bloqueado é um botão dentro de uma tela que a pessoa está vendo.
    return daTela.motivo === 'permissao'
      ? { ...daTela, explicacao: 'Seu perfil não inclui esta ação.' }
      : daTela;
  }

  const modulo = exigencia.modulo;
  if (!modulo || manifesto.modulos.includes(modulo)) return daTela;
  if (acaoLiberadaSemModulo(acaoDeclarada(permissoes[0]))) return daTela;

  return {
    liberado: false,
    motivo: 'modulo',
    explicacao: `O módulo ${NOME_DO_MODULO[modulo]} não está contratado.`,
    moduloEncerrado: modulo,
  };
}
